import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* ────────────────────────────────────────────────────────────────────────────
   Skeleton primitives — the one loading surface for content in this app.

   Every content-loading state is built from these so they share the same
   shimmer, tokens and reduced-motion behaviour (`.dash-skeleton` in index.css).
   A skeleton is composed per component, from that component's real layout —
   the shapes here are only the vocabulary, never a generic "card" stand-in.

   Accessibility: wrap a loading layout in <SkeletonRegion label="…">. It marks
   the region `aria-busy`, announces the label once to screen readers, and hides
   the decorative shapes. The shapes themselves are also `aria-hidden`, so a
   stray one outside a region is still silent.

   Actions (submitting a form, uploading a file) keep a compact inline spinner;
   a skeleton there would suggest content is on its way when it isn't.
   ──────────────────────────────────────────────────────────────────────────── */
import React from 'react';
import { cn } from '@/utils/class-utils';
/** A single placeholder shape. Size and radius come from `className`. */
export const Skeleton = ({ className, style, inverse, children }) => (_jsx("div", { "aria-hidden": 'true', className: cn('dash-skeleton', inverse && 'dash-skeleton-inverse', className), style: style, children: children }));
/**
 * Natural paragraph line lengths — a ragged right edge reads as text, a column
 * of equal bars reads as a table. Deterministic, so SSR/prerender and the
 * client paint the same widths.
 */
const PARAGRAPH_WIDTHS = ['100%', '94%', '97%', '88%', '92%', '62%'];
export const SkeletonText = ({ lines = 3, widths, lineClassName = 'h-3', gapClassName = 'gap-2', className, }) => {
    const list = widths !== null && widths !== void 0 ? widths : Array.from({ length: lines }, (_, i) => lines > 1 && i === lines - 1
        ? PARAGRAPH_WIDTHS[PARAGRAPH_WIDTHS.length - 1]
        : PARAGRAPH_WIDTHS[i % (PARAGRAPH_WIDTHS.length - 1)]);
    return (_jsx("div", { "aria-hidden": 'true', className: cn('flex flex-col', gapClassName, className), children: list.map((w, i) => (_jsx("div", { className: cn('dash-skeleton rounded', lineClassName), style: { width: w } }, i))) }));
};
/** A round shape — avatars, score rings, status dots. */
export const SkeletonCircle = ({ size, className, inverse, }) => (_jsx(Skeleton, { inverse: inverse, className: cn('rounded-full shrink-0', className), style: { width: size, height: size } }));
const setInert = (el) => el === null || el === void 0 ? void 0 : el.setAttribute('inert', '');
/**
 * The loading region: `aria-busy` + a one-time polite announcement, with the
 * placeholder layout hidden from assistive technology. Takes the layout classes
 * of the container it stands in for, so the swap to real content is in place.
 */
export const SkeletonRegion = ({ label, className, style, children, }) => (_jsxs("div", { role: 'status', "aria-busy": 'true', "aria-live": 'polite', className: className, style: style, children: [_jsx("span", { className: 'sr-only', children: label }), _jsx("div", { "aria-hidden": 'true', className: 'contents', ref: setInert, children: children })] }));
export default Skeleton;
